import { LlmReviewSection } from "@/components/file-detail/LlmReviewSection";
import { getFile } from "@/services/api";
import { useQuery } from "@tanstack/react-query";

const fileTypeLabels: Record<string, string> = {
	work_order: "Наряд",
	completion_act: "Акт",
	rate_book: "Книга расценок",
	estimate: "Смета",
	unclassified: "Не определён",
};

const statusLabels: Record<string, string> = {
	uploaded: "Загружен",
	recognized: "Распознан",
	parsed: "Загружен",
	parsed_with_warnings: "Загружен с предупреждениями",
	error: "Ошибка",
};

function formatFileSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatPeriod(month: number | null, year: number | null): string {
	if (!month && !year) return "—";
	const m = month ? String(month).padStart(2, "0") : "??";
	const y = year ?? "????";
	return `${m}/${y}`;
}

export function FileTab({ fileId }: { fileId: number }) {
	const { data, isLoading, error } = useQuery({
		queryKey: ["file", fileId],
		queryFn: () => getFile(fileId),
	});

	if (isLoading) {
		return (
			<div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">
				Загрузка данных файла...
			</div>
		);
	}

	if (error) {
		const message = error instanceof Error ? error.message : "Не удалось загрузить файл";
		return (
			<div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
				{message}
			</div>
		);
	}

	if (!data) {
		return (
			<div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">
				Файл не найден
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<div>
				<h1 className="text-2xl font-bold text-slate-900">{data.originalName}</h1>
				<p className="mt-1 text-sm text-slate-500">Детали загруженного файла</p>
			</div>

			<div className="grid gap-4 md:grid-cols-2">
				<InfoCard label="ID файла" value={String(data.id)} />
				<InfoCard label="Тип" value={fileTypeLabels[data.fileType] ?? data.fileType} />
				<InfoCard label="Статус" value={statusLabels[data.status] ?? data.status} />
				<InfoCard label="Размер" value={formatFileSize(data.fileSize)} />
				<InfoCard label="Объект" value={data.objectName ?? "—"} />
				<InfoCard label="Период" value={formatPeriod(data.periodMonth, data.periodYear)} />
				<InfoCard label="Лист" value={data.sheetName ?? "—"} />
				<InfoCard
					label="Создан"
					value={new Date(data.createdAt).toLocaleString("ru-RU", {
						day: "2-digit",
						month: "2-digit",
						year: "numeric",
						hour: "2-digit",
						minute: "2-digit",
					})}
				/>
			</div>

			{/* Фича 035 — детальная секция LLM-ревью (только для completion_act). */}
			{data.fileType === "completion_act" && <LlmReviewSection fileId={data.id} />}
		</div>
	);
}

function InfoCard({ label, value }: { label: string; value: string }) {
	return (
		<div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
			<p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
			<p className="mt-1 text-sm font-medium text-slate-900">{value}</p>
		</div>
	);
}
